/**
 * Presentation of a reconciled tape's portfolio metrics: the delinquency table
 * the Deal Overview shows (the Portfolio Snapshot's rows 18–24) and the
 * "over 30 days past due" share the Portfolio page shows. Pure; nothing here
 * changes a bucket's boundaries, only how bands are rolled up for display.
 */

type Bucket = { loanCount: number; principalBalance: number };

/** The bands more than 30 days past due. */
const OVER_30_DPD_BUCKETS = ['dpd31To60', 'dpd61To90', 'dpd91Plus'] as const;

type Over30DpdBucket = typeof OVER_30_DPD_BUCKETS[number];

/** Every band the API returns; they always sum to the tape total. */
type Delinquency = {
  [K in
    | 'current'
    | 'dpd1To10'
    | 'dpd11To30'
    | Over30DpdBucket
    | 'unknown']: Bucket;
};

/** Principal more than 30 days past due. */
export function over30DpdPrincipal(
  delinquency: {
    [K in Over30DpdBucket]: { principalBalance: number };
  },
): number {
  return OVER_30_DPD_BUCKETS.reduce(
    (sum, key) => sum + delinquency[key].principalBalance,
    0,
  );
}

export type DelinquencyRow = { label: string } & Bucket;

function sumBuckets(buckets: Bucket[]): Bucket {
  return buckets.reduce<Bucket>(
    (sum, b) => ({
      loanCount: sum.loanCount + b.loanCount,
      principalBalance: sum.principalBalance + b.principalBalance,
    }),
    { loanCount: 0, principalBalance: 0 },
  );
}

/**
 * The delinquency table: the snapshot's five bands, with 1–10 and 11–30 rolled
 * into 1–30. Unknown (no parseable days past due) is listed only when it holds a
 * loan, since the snapshot has no such column; listing it then keeps the bands
 * footing to `total`.
 */
export function delinquencyTable(
  delinquency: Delinquency,
): { bands: DelinquencyRow[]; total: Bucket; over30Days: Bucket } {
  const bands: DelinquencyRow[] = [
    { label: 'Current', ...delinquency.current },
    {
      label: '1–30 days',
      ...sumBuckets([delinquency.dpd1To10, delinquency.dpd11To30]),
    },
    { label: '31–60 days', ...delinquency.dpd31To60 },
    { label: '61–90 days', ...delinquency.dpd61To90 },
    { label: 'More than 90 days', ...delinquency.dpd91Plus },
    ...(delinquency.unknown.loanCount > 0
      ? [{ label: 'Unknown', ...delinquency.unknown }]
      : []),
  ];
  return {
    bands,
    total: sumBuckets(bands),
    over30Days: sumBuckets(OVER_30_DPD_BUCKETS.map((key) => delinquency[key])),
  };
}

/** Average outstanding principal per loan (snapshot row 11); null with no loans. */
export function averagePrincipal({
  loanCount,
  totalPrincipal,
}: {
  loanCount: number;
  totalPrincipal: number;
}): number | null {
  return loanCount > 0 ? totalPrincipal / loanCount : null;
}
